import {
  FieldClass,
  IDataModelFieldTypeInput,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { assertNotNil } from '../../../../utils/types';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import { useCallback, useMemo } from 'react';
import {
  getDraftCustomColumns,
  getViewModelFields,
  listToHashMapFromKey,
  listToHashMapSkipNulls,
} from '../utils';
import {
  dataModelerAddDraftFieldData,
  dataModelerRemoveDraftFieldData,
  dataModelerUpdateDraftFieldData,
  dataModelerUpdateExistingFieldInput,
  dataModelerUpdateExtendFieldTypeInput,
} from '../../../../redux/slices/dataModeler';
import { batch } from 'react-redux';
import {
  ICdwColumnsData,
  ICdwTableColumnData,
  IColumnsDataInput,
  SortableFieldItem,
} from './types';
import { customDefinitionInputToColumnDefinitionMeta } from '../../../../redux/slices/dataModeler/utils';
import { getFieldColumnSchemaValidator } from '../validation';
import { UnreachableCaseError } from 'ts-essentials';
import { truncateStringWithLength } from '../../../../utils';
import { DESCRIPTION_MAX_LENGTH } from '../../../../config/constants';

export type SortableFieldItemKey = keyof SortableFieldItem;
//
// 'fieldClass' | 'isSelected' | 'isModified' | 'column' | 'type' | 'description' |

const compareItemsFunc = (
  sortColumn: SortableFieldItemKey,
): ((a: SortableFieldItem, b: SortableFieldItem) => number) => {
  return (a, b) => {
    switch (sortColumn) {
      case 'isModified':
      case 'isNew':
        return a.isNew < b.isNew ? 1 : a.isModified < b.isModified ? 1 : -1;
      case 'type':
        return a.type.localeCompare(b.type);
      case 'fieldName':
      case 'column':
      case 'description':
        const aKey = a[sortColumn];
        const bKey = b[sortColumn];
        return aKey.localeCompare(bKey);
      default:
        throw new UnreachableCaseError(sortColumn);
    }
  };
};

const searchMatchesCdwColumnData = (
  trimmedLcQuery: string,
  item: ICdwTableColumnData,
) =>
  trimmedLcQuery === '' ||
  item.column.toLowerCase().includes(trimmedLcQuery) ||
  item.description.toLowerCase().includes(trimmedLcQuery) ||
  item.fieldName.toLowerCase().includes(trimmedLcQuery);

export const filterAndSortColumnsData = (
  columns: ICdwTableColumnData[],
  searchQuery: string,
  sortedBy: SortableFieldItemKey | null,
  sortedDesc: boolean,
): ICdwTableColumnData[] => {
  const trimmedLcQuery = searchQuery.trim().toLowerCase();
  const filtered =
    trimmedLcQuery === ''
      ? [...columns]
      : columns.filter((columnData) =>
          searchMatchesCdwColumnData(trimmedLcQuery, columnData),
        );
  const sortedDescMultiplier = sortedDesc ? -1 : 1;
  if (!sortedBy) {
    return filtered;
  } else {
    const sortFunc = compareItemsFunc(sortedBy);
    return filtered.sort((a, b) => {
      return sortFunc(a, b) * sortedDescMultiplier;
    });
  }
};

const useCdwTableColumnsData = ({
  availableColumnsFromServer,
  viewKey,
  tableKey,
  isEditMode,
}: IColumnsDataInput): ICdwColumnsData => {
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  const dispatch = useAppDispatch();
  const columnNameValidator = getFieldColumnSchemaValidator(null);
  const columns: ICdwTableColumnData[] = useMemo(() => {
    const existingViewFields = getViewModelFields(schemaObj, viewKey);
    const existingViewFieldsByTableColumn = listToHashMapSkipNulls(
      existingViewFields,
      (fieldMeta) => {
        const tableField = fieldMeta.tableFields.find(
          (tf) => tf.tableKey === tableKey,
        );
        return tableField?.column ?? null;
      },
    );
    const updateFieldInputsByFieldKey = listToHashMapFromKey(
      draftEdits.updatedFieldsInputs,
      'key',
    );

    const draftTableFieldsByColumn = listToHashMapSkipNulls(
      draftEdits.draftFieldsData.filter(
        (draftField) =>
          draftField.viewKey === viewKey && draftField.tableKey === tableKey,
      ),
      (draftField) => draftField.column.column,
    );
    const addedCustomColumns = getDraftCustomColumns(
      viewKey,
      tableKey,
      draftEdits.draftFieldsData,
    );

    const newDimData = draftEdits.addedDimensionsData.find(
      (newDim) => newDim.viewKey === viewKey,
    );
    const editedJoinData = draftEdits.editJoinsData.find(
      (editedJoin) => editedJoin.viewKey === viewKey,
    );
    const editedColumnsData = listToHashMapFromKey(
      draftEdits.editColumnInputs.filter(
        (editInput) => editInput.tableKey === tableKey,
      ),
      'column',
    );
    const allColumns = [...availableColumnsFromServer, ...addedCustomColumns];
    return allColumns.map((originalTableColumn) => {
      const editColumnData = editedColumnsData[originalTableColumn.column];
      const tableColumn: ITableColumnFieldsFragment = !editColumnData
        ? originalTableColumn
        : {
            ...originalTableColumn,
            typeParams: {
              ...originalTableColumn.typeParams,
              customDefinition: customDefinitionInputToColumnDefinitionMeta(
                originalTableColumn.column,
                editColumnData.customDefinition,
              ),
            },
          };
      const fieldMeta =
        existingViewFieldsByTableColumn[tableColumn.column] ?? null;
      const updateFieldInput = fieldMeta
        ? updateFieldInputsByFieldKey[fieldMeta.key] ?? null
        : null;
      const addFieldInput =
        draftTableFieldsByColumn[tableColumn.column]?.addInput ?? null;
      const extendFieldInput =
        draftTableFieldsByColumn[tableColumn.column]?.extendInput ?? null;
      const isNew = !!addFieldInput;
      const isExtend = !!extendFieldInput;
      const isModified =
        isNew || isExtend || !!updateFieldInput || !!editColumnData;
      // XXX if it was part of an existing temporal jon the field is already there and required by existence
      const isRequiredByModifiedJoin =
        newDimData?.joinData?.temporalCondition?.dimTemporalColumn ===
          tableColumn.column ||
        newDimData?.joinData?.temporalCondition?.dimSecondTemporalColumn ===
          tableColumn.column ||
        editedJoinData?.joinData?.temporalCondition?.dimTemporalColumn ===
          tableColumn.column ||
        editedJoinData?.joinData?.temporalCondition?.dimSecondTemporalColumn ===
          tableColumn.column;
      const isRequiredByClass =
        (addFieldInput &&
          addFieldInput?.typeInput?.fieldClass !== FieldClass.Basic) ||
        (extendFieldInput &&
          extendFieldInput?.typeInput?.fieldClass !== FieldClass.Basic) ||
        false;
      const isRequiredByFunnelPartition =
        !!addFieldInput &&
        draftEdits.addFunnelPartitions.some(
          ({ fieldKey }) => fieldKey === addFieldInput.key,
        );
      // all existing fields are required (can not be unselected)
      const isRequired =
        !!fieldMeta ||
        isRequiredByModifiedJoin ||
        isRequiredByClass ||
        isRequiredByFunnelPartition;
      const isSelected = !!fieldMeta || !!addFieldInput || !!extendFieldInput;

      // name
      const extendingName = extendFieldInput
        ? draftEdits.draftFieldsData.find(
            ({ addInput }) => addInput?.key === extendFieldInput.fieldKey,
          )?.addInput?.name ??
          schemaObj.fact.fields.find(
            (fieldMeta) => fieldMeta.key === extendFieldInput.fieldKey,
          )?.name
        : null;
      const fieldName =
        addFieldInput?.name ??
        updateFieldInput?.name ??
        fieldMeta?.name ??
        extendingName ??
        '';

      const setFieldName = (newName: string) => {
        if (!isEditMode) {
          return;
        }
        if (addFieldInput) {
          dispatch(
            dataModelerUpdateDraftFieldData({
              viewKey,
              tableKey,
              column: tableColumn.column,
              input: {
                name: newName,
              },
            }),
          );
        }
        if (fieldMeta) {
          dispatch(
            dataModelerUpdateExistingFieldInput({
              fieldMeta,
              input: {
                key: fieldMeta.key,
                name: newName,
              },
            }),
          );
        }
      };

      // description
      const description = truncateStringWithLength(
        addFieldInput?.description ??
          updateFieldInput?.description ??
          fieldMeta?.description ??
          tableColumn.description ??
          '',
        DESCRIPTION_MAX_LENGTH,
      );
      const setDescription = (newDescription: string) => {
        if (!isEditMode) {
          return;
        }
        if (addFieldInput) {
          dispatch(
            dataModelerUpdateDraftFieldData({
              viewKey,
              tableKey,
              column: tableColumn.column,
              input: {
                description: newDescription,
              },
            }),
          );
        }
        if (fieldMeta) {
          dispatch(
            dataModelerUpdateExistingFieldInput({
              fieldMeta,
              input: {
                key: fieldMeta.key,
                description: newDescription,
              },
            }),
          );
        }
      };
      const fieldKey =
        fieldMeta?.key ?? addFieldInput?.key ?? extendFieldInput?.fieldKey;
      const unionCount =
        (fieldMeta?.tableFields?.length ?? 0) +
        (draftEdits?.draftFieldsData?.filter(
          (draft) =>
            draft.addInput?.key === fieldKey ||
            draft.extendInput?.fieldKey === fieldKey,
        )?.length ?? 0);

      // Allow overriding type for new basic fields
      const typeInput =
        addFieldInput?.typeInput ?? extendFieldInput?.typeInput ?? null;
      // read-only if already extended
      const setTypeInput =
        !!typeInput && typeInput.fieldClass === FieldClass.Basic
          ? (newInput: IDataModelFieldTypeInput) => {
              if (addFieldInput) {
                dispatch(
                  dataModelerUpdateDraftFieldData({
                    viewKey,
                    tableKey,
                    column: tableColumn.column,
                    input: {
                      typeInput: newInput,
                    },
                  }),
                );
              }
              if (extendFieldInput) {
                dispatch(
                  dataModelerUpdateExtendFieldTypeInput({
                    tableKey,
                    column: tableColumn.column,
                    input: newInput,
                  }),
                );
              }
            }
          : undefined;

      const isUnmapableColumnName = !columnNameValidator.safeParse(
        tableColumn.column,
      ).success;

      return {
        tableColumn,
        originalTableColumn,
        fieldMeta,
        updateFieldInput,
        addFieldInput,
        extendFieldInput,
        isNew,
        isExtend,
        isModified,
        isRequired,
        isSelected,
        column: tableColumn.column,
        type: tableColumn.type,
        description,
        setDescription,
        fieldName,
        setFieldName,
        unionCount,
        typeInput,
        setTypeInput,
        isUnmappableColumnName: isUnmapableColumnName,
      };
    });
  }, [
    availableColumnsFromServer,
    dispatch,
    draftEdits.addedDimensionsData,
    draftEdits.draftFieldsData,
    draftEdits.editJoinsData,
    draftEdits.updatedFieldsInputs,
    draftEdits.addFunnelPartitions,
    draftEdits.editColumnInputs,
    isEditMode,
    schemaObj,
    tableKey,
    viewKey,
    columnNameValidator,
  ]);

  const unmappableColumns = useMemo(() => {
    return new Set(
      columns.flatMap(({ column, isUnmappableColumnName }) =>
        isUnmappableColumnName ? [column] : [],
      ),
    );
  }, [columns]);

  const selectColumns = useCallback(
    (tableColumns: ITableColumnFieldsFragment[]) => {
      batch(() => {
        for (let column of tableColumns) {
          if (!unmappableColumns.has(column.column)) {
            dispatch(
              dataModelerAddDraftFieldData({ viewKey, tableKey, column }),
            );
          }
        }
      });
    },
    [dispatch, tableKey, viewKey, unmappableColumns],
  );

  const unselectColumns = useCallback(
    (tableColumns: ITableColumnFieldsFragment[]) => {
      batch(() => {
        for (let { column } of tableColumns) {
          dispatch(
            dataModelerRemoveDraftFieldData({ viewKey, tableKey, column }),
          );
        }
      });
    },
    [dispatch, tableKey, viewKey],
  );

  return {
    columns,
    selectColumns,
    unselectColumns,
  };
};

export default useCdwTableColumnsData;
